import moment from "moment-timezone";
import i18n from "i18next";
import { buildConfirmationSummary } from "./registrationConfirmationMock";
import {
  getAppointmentDietitianName,
  getRegistrationTimezone,
} from "./calendlyBooking";
import {
  isInPersonModality,
  mapSlotLocationToLabel,
} from "../Dietitian/registrationMatchedRd";

const DEFAULT_DURATION_MINUTES = 50;

const escapeIcsText = (value) =>
  String(value ?? "")
    .replace(/\\/g, "\\\\")
    .replace(/;/g, "\\;")
    .replace(/,/g, "\\,")
    .replace(/\r\n/g, "\\n")
    .replace(/\n/g, "\\n");

const formatIcsUtc = (dateMoment) =>
  dateMoment.utc().format("YYYYMMDD[T]HHmmss[Z]");

const parseAppointmentStart = (registrationData, calendlyAppointment) => {
  const tz = getRegistrationTimezone();
  const selectedAppointment = registrationData?.selectedAppointment;

  const rawStart =
    calendlyAppointment?.start_time ??
    calendlyAppointment?.startTime ??
    calendlyAppointment?.scheduled_at ??
    calendlyAppointment?.scheduledAt ??
    selectedAppointment?.startTime ??
    null;

  if (rawStart) {
    const parsed = moment.tz(rawStart, tz);
    if (parsed.isValid()) {
      return parsed;
    }

    const fallbackParsed = moment(rawStart);
    if (fallbackParsed.isValid()) {
      return fallbackParsed.tz(tz);
    }
  }

  if (selectedAppointment?.dateKey && selectedAppointment?.time) {
    const combined = moment.tz(
      `${selectedAppointment.dateKey} ${selectedAppointment.time}`,
      "YYYY-MM-DD h:mm A",
      tz
    );
    if (combined.isValid()) {
      return combined;
    }
  }

  return null;
};

export const buildAppointmentCalendarEvent = ({
  registrationData,
  calendlyAppointment = null,
  durationMinutes = DEFAULT_DURATION_MINUTES,
}) => {
  const summary = buildConfirmationSummary(
    registrationData,
    calendlyAppointment
  );
  const start = parseAppointmentStart(registrationData, calendlyAppointment);

  if (!start) {
    return null;
  }

  const end = start.clone().add(durationMinutes, "minutes");
  const dietitianName = getAppointmentDietitianName(
    calendlyAppointment,
    summary.dietitianDisplay
  );
  const confirmationNumber =
    calendlyAppointment?.confirmation_number ??
    calendlyAppointment?.confirmationNumber ??
    registrationData?.confirmationNumber ??
    summary.confirmationNumber;

  const slotLocation =
    registrationData?.selectedAppointment?.location ??
    registrationData?.selectedAppointment?.raw?.location;
  const modalityLabel = mapSlotLocationToLabel(slotLocation);
  const isInPerson = isInPersonModality(modalityLabel);

  const descriptionLines = [
    i18n.t("NUTRITION_CALENDAR_EVENT_LBL_DIETITIAN", { name: dietitianName }),
    isInPerson
      ? i18n.t("NUTRITION_CALENDAR_EVENT_DESC_IN_PERSON")
      : i18n.t("NUTRITION_CALENDAR_EVENT_DESC_TELEHEALTH"),
    confirmationNumber
      ? i18n.t("NUTRITION_CALENDAR_EVENT_LBL_CONFIRMATION", {
          number: confirmationNumber,
        })
      : null,
    i18n.t("NUTRITION_CALENDAR_EVENT_DESC_DOWNLOAD_APP"),
  ].filter(Boolean);

  return {
    title: i18n.t("NUTRITION_CALENDAR_EVENT_TITLE"),
    description: descriptionLines.join("\n"),
    location: isInPerson
      ? i18n.t("NUTRITION_COMMON_LBL_IN_PERSON")
      : i18n.t("NUTRITION_CALENDAR_EVENT_LBL_TELEHEALTH"),
    start,
    end,
    uid: `husk-appointment-${confirmationNumber || start.format("YYYYMMDDTHHmmss")}@husknutrition.com`,
  };
};

export const buildIcsContent = (event) => {
  if (!event) {
    return null;
  }

  const now = moment.utc();

  return [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    "PRODID:-//HUSK Nutrition//Appointment//EN",
    "BEGIN:VEVENT",
    `UID:${event.uid}`,
    `DTSTAMP:${formatIcsUtc(now)}`,
    `DTSTART:${formatIcsUtc(event.start)}`,
    `DTEND:${formatIcsUtc(event.end)}`,
    `SUMMARY:${escapeIcsText(event.title)}`,
    `DESCRIPTION:${escapeIcsText(event.description)}`,
    `LOCATION:${escapeIcsText(event.location)}`,
    "END:VEVENT",
    "END:VCALENDAR",
  ].join("\r\n");
};

export const buildGoogleCalendarUrl = (event) => {
  const formatGoogleDate = (dateMoment) => formatIcsUtc(dateMoment);
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: event.title,
    dates: `${formatGoogleDate(event.start)}/${formatGoogleDate(event.end)}`,
    details: event.description,
    location: event.location,
  });

  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};

export const buildOutlookCalendarUrl = (event) => {
  const params = new URLSearchParams({
    path: "/calendar/action/compose",
    rru: "addevent",
    subject: event.title,
    startdt: event.start.toISOString(),
    enddt: event.end.toISOString(),
    body: event.description,
    location: event.location,
  });

  return `https://outlook.live.com/calendar/0/deeplink/compose?${params.toString()}`;
};

const triggerIcsBlob = (icsContent, { download = false } = {}) => {
  const blob = new Blob([icsContent], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);

  if (download) {
    const link = document.createElement("a");
    link.href = url;
    link.download = "husk-nutrition-appointment.ics";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    return;
  }

  window.open(url, "_blank", "noopener,noreferrer");
  window.setTimeout(() => URL.revokeObjectURL(url), 60000);
};

export const CALENDAR_PROVIDER_OPTIONS = [
  { id: "google", label: "Google Calendar" },
  { id: "outlook", label: "Outlook Calendar" },
  { id: "apple", label: "Apple Calendar" },
  { id: "ics", label: "Download ICS file" },
];

export const openAppointmentCalendarOption = ({
  provider,
  registrationData,
  calendlyAppointment = null,
}) => {
  const event = buildAppointmentCalendarEvent({
    registrationData,
    calendlyAppointment,
  });

  if (!event) {
    return { success: false, error: "NUTRITION_CALENDAR_ERR_MISSING_DATETIME" };
  }

  if (provider === "google") {
    window.open(
      buildGoogleCalendarUrl(event),
      "_blank",
      "noopener,noreferrer"
    );
    return { success: true };
  }

  if (provider === "outlook") {
    window.open(
      buildOutlookCalendarUrl(event),
      "_blank",
      "noopener,noreferrer"
    );
    return { success: true };
  }

  const icsContent = buildIcsContent(event);
  if (!icsContent) {
    return { success: false, error: "NUTRITION_CALENDAR_ERR_ADD" };
  }

  if (provider === "apple") {
    triggerIcsBlob(icsContent, { download: false });
    return { success: true };
  }

  if (provider === "ics") {
    triggerIcsBlob(icsContent, { download: true });
    return { success: true };
  }

  return { success: false, error: "NUTRITION_CALENDAR_ERR_UNKNOWN_OPTION" };
};

export const downloadAppointmentIcs = ({
  registrationData,
  calendlyAppointment = null,
}) =>
  openAppointmentCalendarOption({
    provider: "ics",
    registrationData,
    calendlyAppointment,
  });
